// The Agents list (Chief of Staff mode): every sub-agent and workflow a chat
// launched, derived from its messages and never stored. Like the Notebook's
// `deriveNotebookCells`, it is a pure fold, so live and replayed chats list the
// same agents; the reducer stays the one codepath that builds the messages.
//
// Also here: the two ways a delegated agent's conversation becomes message
// rows for the read-only drawer. A sub-agent's is in its tool call
// (`toolCall.subagent`); a workflow agent's comes from the runner's saved
// transcript.

import type {
  AgentMessage,
  SubagentInfo,
  ToolCallInfo,
  WorkflowAgentInfo,
  WorkflowInfo,
  WorkflowStep,
  WorkflowTranscript,
} from './types.js'
import { isSubagentTool, isWorkflowTool } from './agentEventReducer.js'
import { formatMcpToolName } from './tool-formatters.js'

export type DelegatedEntry =
  | { kind: 'subagent'; toolCallId: string; toolCall: ToolCallInfo; info: SubagentInfo }
  | { kind: 'workflow'; toolCallId: string; toolCall: ToolCallInfo; info: WorkflowInfo }

// One cache per message object: a streaming delta replaces only the message
// it touches, so every other message's entries keep their identity.
const entriesByMessage = new WeakMap<AgentMessage, DelegatedEntry[]>()

function entriesFor(message: AgentMessage): DelegatedEntry[] {
  const cached = entriesByMessage.get(message)
  if (cached) return cached
  const entries: DelegatedEntry[] = []
  // Only the message's own tool calls: a sub-agent launched inside a
  // sub-agent lives in that sub-agent's `toolCalls` and is not listed.
  for (const tc of message.toolCalls ?? []) {
    if (tc.subagent || isSubagentTool(tc.name)) {
      if (tc.subagent) entries.push({ kind: 'subagent', toolCallId: tc.id, toolCall: tc, info: tc.subagent })
    } else if (tc.workflow || isWorkflowTool(tc.name)) {
      if (tc.workflow) entries.push({ kind: 'workflow', toolCallId: tc.id, toolCall: tc, info: tc.workflow })
    }
  }
  entriesByMessage.set(message, entries)
  return entries
}

/** Every sub-agent and workflow the chat launched, in chat order. */
export function deriveDelegatedAgents(messages: AgentMessage[]): DelegatedEntry[] {
  const out: DelegatedEntry[] = []
  for (const m of messages) {
    if (m.role !== 'assistant' || !m.toolCalls?.length) continue
    out.push(...entriesFor(m))
  }
  return out
}

/**
 * A sub-agent's conversation as two rows: its task prompt from the user's
 * side, and everything it did as one assistant row. Its closing result is
 * appended unless the text already ends with it.
 */
export function subagentToMessages(toolCall: ToolCallInfo): AgentMessage[] {
  const info = toolCall.subagent
  const prompt = typeof toolCall.input?.prompt === 'string' ? toolCall.input.prompt : ''
  const rows: AgentMessage[] = []
  if (prompt) rows.push({ id: `${toolCall.id}:prompt`, role: 'user', content: prompt, timestamp: '' })
  if (!info) return rows
  let content = info.text
  const result = info.result?.trim()
  if (result && !content.trimEnd().endsWith(result)) {
    content = content.trim() ? `${content.trimEnd()}\n\n${result}` : result
  }
  rows.push({
    id: `${toolCall.id}:work`,
    role: 'assistant',
    content,
    thinking: info.thinking || undefined,
    toolCalls: info.toolCalls,
    timestamp: '',
  })
  return rows
}

/**
 * A workflow agent's saved transcript as message rows. Each `tool_use` is
 * paired with the `tool_result` that answers it into one tool call
 * (`running` until it has one). A user entry that only carries tool results
 * makes no row; consecutive assistant entries (the CLI writes one line per
 * content block) make one. A row's id is its first entry's index, so it does
 * not change as the transcript grows.
 */
export function transcriptToMessages(t: WorkflowTranscript): AgentMessage[] {
  // First pass: every tool result, by the tool_use it answers.
  const results = new Map<string, { text: string; isError: boolean }>()
  for (const entry of t.entries) {
    for (const b of entry.blocks) {
      if (b.type === 'tool_result' && b.tool_use_id) {
        results.set(b.tool_use_id, { text: b.text ?? '', isError: b.is_error === true })
      }
    }
  }

  const rows: AgentMessage[] = []
  let open: AgentMessage | null = null // the assistant row consecutive entries add to
  t.entries.forEach((entry, i) => {
    const id = `${t.agent_id}:${i}`
    const timestamp = entry.timestamp ?? ''
    if (entry.role === 'user') {
      // Any user entry ends the assistant row, so text and the tools around
      // it keep their order; one with only tool results makes no row itself.
      const text = joinText(entry.blocks.filter(b => b.type === 'text').map(b => b.text))
      const task = text ? stripHarnessPreamble(text) : text
      if (task) rows.push({ id, role: 'user', content: task, timestamp })
      open = null
      return
    }
    const text = joinText(entry.blocks.filter(b => b.type === 'text').map(b => b.text))
    const thinking = joinText(entry.blocks.filter(b => b.type === 'thinking').map(b => b.text))
    const toolCalls: ToolCallInfo[] = entry.blocks
      .filter(b => b.type === 'tool_use')
      .map((b, j) => {
        const callId = b.tool_use_id || `${id}:tool:${j}`
        const result = results.get(callId)
        const call: ToolCallInfo = {
          id: callId,
          name: b.name ?? '',
          input: b.input ?? {},
          status: result ? (result.isError ? 'error' : 'complete') : 'running',
        }
        if (result) call.output = result.text
        return call
      })
    if (!text && !thinking && toolCalls.length === 0) return
    const current: AgentMessage | null = open
    if (current) {
      current.content = joinText([current.content, text])
      current.thinking = joinText([current.thinking, thinking]) || undefined
      if (toolCalls.length) current.toolCalls = [...(current.toolCalls ?? []), ...toolCalls]
      return
    }
    const row: AgentMessage = { id, role: 'assistant', content: text, timestamp }
    if (thinking) row.thinking = thinking
    if (toolCalls.length) row.toolCalls = toolCalls
    rows.push(row)
    open = row
  })
  return rows
}

function joinText(parts: (string | undefined)[]): string {
  return parts.filter((p): p is string => !!p && p.trim() !== '').join('\n\n')
}

const HARNESS_PREAMBLE = '[Workflow harness — computed task]'
const HARNESS_FOLLOWS = 'The computed task text follows:'

/**
 * A workflow agent's task as the script wrote it: the CLI puts a fixed
 * preamble before the computed text and indents every line of it. Text
 * without the preamble is returned as it is.
 */
export function stripHarnessPreamble(text: string): string {
  if (!text.startsWith(HARNESS_PREAMBLE)) return text
  const at = text.indexOf(HARNESS_FOLLOWS)
  if (at < 0) return text
  const body = text.slice(at + HARNESS_FOLLOWS.length).replace(/^\n/, '')
  const lines = body.split('\n')
  const indent = Math.min(...lines.filter(l => l.trim()).map(l => l.length - l.trimStart().length))
  return lines.map(l => l.slice(Number.isFinite(indent) ? indent : 0)).join('\n').trim()
}

// ── What an agent is doing now (the Agents list's live line) ──────────────

/** The verb a tool reads as in the live line; any other tool by its name. */
const STEP_VERBS: Record<string, string> = {
  Bash: 'Running', Read: 'Reading', Write: 'Writing', Edit: 'Editing', Grep: 'Searching', Glob: 'Searching',
  WebFetch: 'Fetching', WebSearch: 'Searching the web', Skill: 'Using a skill', StructuredOutput: 'Reporting back',
  TodoWrite: 'Planning',
}

/** The input keys that say what a tool call does, in the order looked for (the runner's `toolGist`). */
const GIST_KEYS = ['command', 'description', 'file_path', 'path', 'pattern', 'url', 'query', 'prompt']

function oneLine(s: string, max = 200): string {
  const t = s.replace(/\\\n/g, ' ').split(/\s+/).filter(Boolean).join(' ')
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

function stepLine(kind: WorkflowStep['kind'], tool: string | undefined, text: string | undefined): string | undefined {
  if (kind === 'tool') {
    const verb = (tool && STEP_VERBS[tool]) || (tool ? formatMcpToolName(tool) : 'Working')
    return text ? `${verb}: ${text}` : verb
  }
  if (!text) return undefined
  return kind === 'thinking' ? `Thinking: ${text}` : `Writing: ${text}`
}

/** A workflow agent's live line: its newest step while it runs, its result once done. */
export function workflowAgentNow(agent: WorkflowAgentInfo): string | undefined {
  if (agent.result != null && agent.result !== '') return resultLine(agent.result)
  const step = agent.last_step
  return step ? stepLine(step.kind, step.tool, step.text) : undefined
}

/** A direct sub-agent's live line: its newest tool call while it runs, else its progress summary. */
export function subagentNow(info: SubagentInfo): string | undefined {
  if (info.status === 'running') {
    const last = info.toolCalls[info.toolCalls.length - 1]
    if (last) {
      const gist = GIST_KEYS.map(k => last.input?.[k]).find((v): v is string => typeof v === 'string' && v.trim() !== '')
      return stepLine('tool', last.name, gist ? oneLine(gist) : undefined)
    }
  }
  return info.summary ? oneLine(info.summary) : undefined
}

/** The result fields that read as a one-line answer, in the order looked for. */
const RESULT_LINE_KEYS = ['headline', 'summary', 'result', 'answer', 'message']

/**
 * A finished agent's result as one line. An agent given a schema returns an
 * object: its headline (or summary…) is shown, else "Done" rather than JSON.
 */
export function resultLine(result: string): string {
  const t = result.trim()
  if (t.startsWith('{')) {
    try {
      const o = JSON.parse(t) as Record<string, unknown>
      const v = RESULT_LINE_KEYS.map(k => o[k]).find((x): x is string => typeof x === 'string' && x.trim() !== '')
      return v ? oneLine(v) : 'Done'
    } catch { /* not JSON after all */ }
  }
  return oneLine(t)
}

